import React, { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiBase } from '../lib/utils'
import { api, systemPromptsApi, SystemPromptListItem } from '../lib/api'
import AgentMCPDrawer from '../components/AgentMCPDrawer'
import WhatsAppTab from '../components/WhatsAppTab'
import AgentSandboxChat, { type AgentSummary } from '../components/AgentSandboxChat'
import AgentEditorModal from '../components/AgentEditorModal'
import { HelpTooltip } from '../components/Tooltip'
import { useAgentTeams } from '../hooks/useAgentTeams'
import TeamsSidebar from '../components/agents/TeamsSidebar'
import AgentOnboardingGuide from '../components/onboarding/AgentOnboardingGuide'

const numberFormatter = new Intl.NumberFormat('fr-FR')
const dateFormatter = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })

type Agent = {
  id: number
  owner_user_id: number
  name: string
  slug: string
  icon_url?: string | null
  icon_preview_url?: string | null
  subtitle?: string | null
  instructions?: string | null
  message_limit?: number | null
  model_id?: string | null
  top_k?: number | null
  rerank_top_n: number
  disable_own_knowledge: boolean
  is_active: boolean
  subject_ids: number[]
  team_id?: number | null
  system_prompt_id?: number | null  // Admin-only: override system prompt
  usage: { message_count: number; limit?: number | null }
  owner_usage: { message_count: number; limit?: number | null }
  created_at: string
  updated_at: string
}

type AgentQuota = {
  max_active: number | null
  current_active: number
  available: number | null
  total_agents: number
}

type Subject = { id: number; title: string; source: string }

type FormState = {
  name: string
  subtitle: string
  instructions: string
  messageLimit: string
  modelId: string
  topK: string
  rerankTopN: string
  disableOwnKnowledge: boolean
  subjectIds: number[]
  systemPromptId: number | null  // Admin-only
  teamId: number | null
}

const emptyForm: FormState = {
  name: '',
  subtitle: '',
  instructions: '',
  messageLimit: '',
  modelId: '',
  topK: '10',
  rerankTopN: '5',
  disableOwnKnowledge: false,
  subjectIds: [],
  systemPromptId: null,
  teamId: null,
}

export default function AgentsPage() {
  const navigate = useNavigate()
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [quota, setQuota] = useState<AgentQuota | null>(null)
  const [quotaLoading, setQuotaLoading] = useState(false)

  const [subjects, setSubjects] = useState<Subject[]>([])
  const [subjectsLoading, setSubjectsLoading] = useState(false)

  const [ollamaModels, setOllamaModels] = useState<string[]>([])
  const [ollamaModelsLoading, setOllamaModelsLoading] = useState(false)

  // Current user (for admin check)
  const [currentUser, setCurrentUser] = useState<{ is_superuser?: boolean } | null>(null)
  const isAdmin = currentUser?.is_superuser === true

  // System prompts (admin-only)
  const [systemPrompts, setSystemPrompts] = useState<SystemPromptListItem[]>([])

  // Agent teams
  const { 
    teams, 
    isLoading: teamsLoading,
    create: createTeam,
    rename: renameTeam,
    remove: removeTeam,
    refetch: refetchTeams,
  } = useAgentTeams()
  const [selectedTeamId, setSelectedTeamId] = useState<number | 'orphan' | null>(null)

  const [createForm, setCreateForm] = useState<FormState>(emptyForm)
  const [createIcon, setCreateIcon] = useState<File | null>(null)
  const [creating, setCreating] = useState(false)
  const [createFeedback, setCreateFeedback] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  const [teamModalOpen, setTeamModalOpen] = useState(false)
  const [statsDrawerOpen, setStatsDrawerOpen] = useState(false)
  const [mcpDrawerOpen, setMcpDrawerOpen] = useState(false)
  const [mcpDrawerAgentId, setMcpDrawerAgentId] = useState<number | null>(null)
  const [mcpDrawerAgentName, setMcpDrawerAgentName] = useState('')

  const [editingAgent, setEditingAgent] = useState<Agent | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editForm, setEditForm] = useState<FormState>(emptyForm)
  const [editError, setEditError] = useState<string | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)
  const [sandboxConversationId, setSandboxConversationId] = useState<number | null>(null)

  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null)
  const [deletePending, setDeletePending] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [iconUploadingId, setIconUploadingId] = useState<number | null>(null)
  const iconUrlsRef = useRef<string[]>([])

  const loadAgents = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get('/agents')
      const items: Agent[] = Array.isArray(data?.items) ? data.items : []

      const nextBlobUrls: string[] = []
      const enriched: Agent[] = await Promise.all(
        items.map(async (agent) => {
          if (agent.icon_url) {
            try {
              const response = await api.get(agent.icon_url, { responseType: 'blob' })
              const blob: Blob = response.data as Blob
              const objectUrl = URL.createObjectURL(blob)
              nextBlobUrls.push(objectUrl)
              return { ...agent, icon_preview_url: objectUrl }
            } catch {
              return { ...agent, icon_preview_url: null }
            }
          }
          return { ...agent, icon_preview_url: null }
        })
      )

      iconUrlsRef.current.forEach((url) => {
        try { URL.revokeObjectURL(url) } catch {}
      })
      iconUrlsRef.current = nextBlobUrls

      setAgents(enriched)
      try {
        window.dispatchEvent(new CustomEvent('agents-updated', { detail: { ids: enriched.map(agent => agent.id) } }))
      } catch (eventError) {
        console.warn('[AgentsPage] dispatch agents-updated failed', eventError)
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur chargement agents'
      setError(detail)
      setAgents([])
    } finally {
      setLoading(false)
    }
  }, [])

  const loadQuota = useCallback(async () => {
    setQuotaLoading(true)
    try {
      const { data } = await api.get('/agents/quota')
      setQuota(data as AgentQuota)
    } catch (err: any) {
      console.warn('[AgentsPage] Failed to load quota:', err)
      setQuota(null)
    } finally {
      setQuotaLoading(false)
    }
  }, [])

  const loadSubjects = useCallback(async () => {
    setSubjectsLoading(true)
    try {
      const { data } = await api.get('/subjects')
      if (Array.isArray(data)) setSubjects(data as Subject[])
      else if (Array.isArray(data?.items)) setSubjects(data.items as Subject[])
      else setSubjects([])
    } catch {
      setSubjects([])
    } finally {
      setSubjectsLoading(false)
    }
  }, [])

  const loadOllamaModels = useCallback(async () => {
    setOllamaModelsLoading(true)
    try {
      const { data } = await api.get('/models')
      if (data?.models && Array.isArray(data.models)) {
        const modelNames = data.models.map((m: any) => m.name).filter(Boolean)
        setOllamaModels(modelNames)
      } else {
        setOllamaModels([])
      }
    } catch {
      setOllamaModels([])
    } finally {
      setOllamaModelsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadAgents()
    loadQuota()
    loadSubjects()
    loadOllamaModels()
    // Load current user
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null')
      setCurrentUser(user)
    } catch {}
  }, [loadAgents, loadQuota, loadSubjects, loadOllamaModels])

  // Load system prompts if admin
  useEffect(() => {
    if (!isAdmin) return
    systemPromptsApi.list().then(result => {
      setSystemPrompts(result.items)
    }).catch(err => {
      console.warn('[AgentsPage] Failed to load system prompts:', err)
    })
  }, [isAdmin])

  useEffect(() => {
    return () => {
      iconUrlsRef.current.forEach((url) => {
        try { URL.revokeObjectURL(url) } catch {}
      })
      iconUrlsRef.current = []
    }
  }, [])

  const ownerUsage = agents[0]?.owner_usage ?? null
  const subjectMap = useMemo(() => {
    const map = new Map<number, Subject>()
    subjects.forEach(s => map.set(s.id, s))
    return map
  }, [subjects])

  // Agent counts by team
  const agentCountByTeam = useMemo(() => {
    const counts: Record<number, number> = {}
    for (const agent of agents) {
      if (agent.team_id) {
        counts[agent.team_id] = (counts[agent.team_id] || 0) + 1
      }
    }
    return counts
  }, [agents])

  // Orphan agents count (no team)
  const orphanAgentCount = useMemo(() => {
    return agents.filter(a => !a.team_id).length
  }, [agents])

  // Filtered agents based on selected team
  const filteredAgents = useMemo(() => {
    if (selectedTeamId === null) return agents
    if (selectedTeamId === 'orphan') return agents.filter(a => !a.team_id)
    return agents.filter(a => a.team_id === selectedTeamId)
  }, [agents, selectedTeamId])

  useEffect(() => {
    if (!editingAgent) return
    const latest = agents.find(a => a.id === editingAgent.id)
    if (latest) {
      setEditingAgent(latest)
    }
  }, [agents, editingAgent])

  // Listen for onboarding event to close sandbox modal
  useEffect(() => {
    const handleCloseSandbox = () => {
      setIsEditModalOpen(false)
      setEditingAgent(null)
      setEditForm(emptyForm)
      setEditError(null)
      setSandboxConversationId(null)
    }
    window.addEventListener('onboarding:close-sandbox', handleCloseSandbox)
    return () => window.removeEventListener('onboarding:close-sandbox', handleCloseSandbox)
  }, [])

  const overviewStats = useMemo(() => {
    const ownerCount = ownerUsage?.message_count ?? 0
    const ownerLimit = ownerUsage?.limit ?? null
    const remaining = ownerLimit != null ? Math.max(ownerLimit - ownerCount, 0) : null
    return [
      {
        label: 'Agents actifs',
        value: numberFormatter.format(agents.length),
        description: agents.length > 0 ? 'Agents prêts à répondre dans le chat' : 'Créez votre premier agent personnalisé',
      },
      {
        label: 'Messages envoyés',
        value: numberFormatter.format(ownerCount),
        description: ownerLimit != null ? `Reste ${numberFormatter.format(remaining ?? 0)} messages` : 'Aucun plafond défini',
      },
      {
        label: 'Quota global',
        value: ownerLimit != null ? numberFormatter.format(ownerLimit) : '∞',
        description: ownerLimit != null ? 'Limite propriétaire' : 'Plan sans limite',
      },
      {
        label: 'Dernière mise à jour',
        value: agents.length ? dateFormatter.format(new Date(agents[0].updated_at)) : '—',
        description: agents.length ? 'Agent le plus récent' : 'Aucun agent pour le moment',
      },
    ]
  }, [agents, ownerUsage])

  const updateCreateSubjectIds = (id: number) => {
    setCreateForm(prev => {
      const next = prev.subjectIds.includes(id)
        ? prev.subjectIds.filter(sid => sid !== id)
        : [...prev.subjectIds, id]
      return { ...prev, subjectIds: next }
    })
  }

  const updateEditSubjectIds = (id: number) => {
    setEditForm(prev => {
      const next = prev.subjectIds.includes(id)
        ? prev.subjectIds.filter(sid => sid !== id)
        : [...prev.subjectIds, id]
      return { ...prev, subjectIds: next }
    })
  }

  const resetCreateForm = () => {
    setCreateForm(emptyForm)
    setCreateIcon(null)
  }

  const openCreateDrawer = () => {
    resetCreateForm()
    setCreateError(null)
    setCreateFeedback(null)
    setCreateDrawerOpen(true)
  }

  const closeCreateDrawer = () => {
    setCreateDrawerOpen(false)
    resetCreateForm()
    setCreateError(null)
  }

  const openStatsDrawer = () => {
    setStatsDrawerOpen(true)
  }

  const closeStatsDrawer = () => {
    setStatsDrawerOpen(false)
  }

  const openMcpDrawer = (agentId: number, agentName: string) => {
    setMcpDrawerAgentId(agentId)
    setMcpDrawerAgentName(agentName)
    setMcpDrawerOpen(true)
  }

  const closeMcpDrawer = () => {
    setMcpDrawerOpen(false)
    setMcpDrawerAgentId(null)
    setMcpDrawerAgentName('')
  }

  const onCreateSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!createForm.name.trim()) {
      setCreateError('Le nom est requis')
      return
    }
    setCreating(true)
    setCreateError(null)
    setCreateFeedback(null)
    try {
      const payload: any = {
        name: createForm.name.trim(),
        subtitle: createForm.subtitle.trim() || undefined,
        instructions: createForm.instructions.trim() || undefined,
        message_limit: createForm.messageLimit ? Number(createForm.messageLimit) : undefined,
        model_id: createForm.modelId.trim() || undefined,
        top_k: createForm.topK ? Number(createForm.topK) : undefined,
        rerank_top_n: createForm.rerankTopN ? Number(createForm.rerankTopN) : 5,
        disable_own_knowledge: createForm.disableOwnKnowledge,
        subject_ids: createForm.subjectIds,
      }
      const { data } = await api.post('/agents', payload)
      if (createIcon) {
        const form = new FormData()
        form.append('file', createIcon)
        await api.post(`/agents/${data.id}/icon`, form)
      }
      await loadAgents()
      setCreateDrawerOpen(false)
      resetCreateForm()
      setCreateFeedback(`Agent « ${data.name} » créé`)
      try {
        localStorage.setItem('chat_selected_agent', String(data.id))
        window.dispatchEvent(new CustomEvent('chat-agent-selected', { detail: data.id }))
      } catch {}
    } catch (err: any) {
      const rawDetail = err?.response?.data?.detail
      // Handle object error responses (quota errors have {error, message, ...})
      const detail = typeof rawDetail === 'object' && rawDetail !== null
        ? rawDetail.message || rawDetail.error || JSON.stringify(rawDetail)
        : rawDetail || err?.message || 'Erreur création agent'
      setCreateError(detail)
    } finally {
      setCreating(false)
    }
  }

  const startEdit = (agent: Agent) => {
    setEditingAgent(agent)
    setIsEditModalOpen(true)
    setEditForm({
      name: agent.name,
      subtitle: agent.subtitle || '',
      instructions: agent.instructions || '',
      messageLimit: agent.message_limit != null ? String(agent.message_limit) : '',
      modelId: agent.model_id || '',
      topK: agent.top_k != null ? String(agent.top_k) : '10',
      rerankTopN: agent.rerank_top_n != null ? String(agent.rerank_top_n) : '5',
      disableOwnKnowledge: agent.disable_own_knowledge || false,
      subjectIds: [...agent.subject_ids],
      systemPromptId: agent.system_prompt_id ?? null,
      teamId: agent.team_id ?? null,
    })
    setEditError(null)
  }

  const cancelEdit = () => {
    setIsEditModalOpen(false)
    setEditingAgent(null)
    setEditForm(emptyForm)
    setEditError(null)
    setSandboxConversationId(null)
  }

  const saveEdit = async () => {
    if (!editingAgent) return
    setSavingEdit(true)
    setEditError(null)
    try {
      // Check if model changed (to allow backend auto-configuration of top_k)
      const modelChanged = editingAgent.model_id !== (editForm.modelId.trim() || null)
      
      const payload: any = {
        name: editForm.name.trim() || undefined,
        subtitle: editForm.subtitle.trim() || null,
        instructions: editForm.instructions.trim() || null,
        message_limit: editForm.messageLimit === '' ? null : Number(editForm.messageLimit),
        model_id: editForm.modelId.trim() || null,
        // top_k: don't send if model changed (let backend auto-configure)
        // Only send if admin AND model didn't change (preserving manual override)
        ...(!modelChanged && isAdmin && { top_k: editForm.topK === '' ? null : Number(editForm.topK) }),
        rerank_top_n: editForm.rerankTopN ? Number(editForm.rerankTopN) : 5,
        disable_own_knowledge: editForm.disableOwnKnowledge,
        subject_ids: editForm.subjectIds,
        // Admin-only: system prompt override
        ...(isAdmin && { system_prompt_id: editForm.systemPromptId }),
        // Team assignment: 0 means remove from team, null means no change
        team_id: editForm.teamId === null ? (editingAgent.team_id ? 0 : undefined) : editForm.teamId,
      }
      const response = await api.patch(`/agents/${editingAgent.id}`, payload)
      await loadAgents()
      // Update editingAgent with new data (don't close modal)
      setEditingAgent(response.data)
      // Update form with new values from server (e.g., auto-configured top_k)
      setEditForm(prev => ({
        ...prev,
        topK: response.data.top_k != null ? String(response.data.top_k) : '10',
        rerankTopN: response.data.rerank_top_n != null ? String(response.data.rerank_top_n) : '5',
        modelId: response.data.model_id || '',
        teamId: response.data.team_id ?? null,
      }))
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur mise à jour agent'
      setEditError(detail)
    } finally {
      setSavingEdit(false)
    }
  }

  const requestDeleteAgent = (agent: Agent) => {
    setDeleteTarget(agent)
    setDeleteError(null)
  }

  const cancelDeleteAgent = () => {
    setDeleteTarget(null)
    setDeleteError(null)
    setDeletePending(false)
  }

  const confirmDeleteAgent = async () => {
    if (!deleteTarget) return
    setDeletePending(true)
    try {
      await api.delete(`/agents/${deleteTarget.id}`)
      if (editingAgent?.id === deleteTarget.id) cancelEdit()
      // Recharger les agents ET le quota
      await Promise.all([loadAgents(), loadQuota()])
      cancelDeleteAgent()
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Suppression impossible'
      setDeleteError(detail)
      setDeletePending(false)
    }
  }

  const toggleAgentActivation = async (agentId: number, currentlyActive: boolean) => {
    try {
      const endpoint = currentlyActive ? 'deactivate' : 'activate'
      await api.post(`/agents/${agentId}/${endpoint}`)
      
      // Recharger les agents et le quota
      await Promise.all([loadAgents(), loadQuota()])
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message
      if (err?.response?.status === 403) {
        // Quota dépassé
        alert(detail || 'Quota d\'agents actifs atteint. Désactivez un agent pour en activer un autre.')
      } else {
        alert(detail || 'Erreur lors de l\'activation/désactivation')
      }
    }
  }

  const uploadIcon = async (agentId: number, file: File) => {
    setIconUploadingId(agentId)
    try {
      const data = new FormData()
      data.append('file', file)
      await api.post(`/agents/${agentId}/icon`, data)
      await loadAgents()
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur upload icône'
      alert(detail)
    } finally {
      setIconUploadingId(null)
    }
  }

  const onIconChange = (agentId: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      uploadIcon(agentId, file)
      event.target.value = ''
    }
  }

  const selectForChat = (agentId: number) => {
    try {
      localStorage.setItem('chat_selected_agent', String(agentId))
      window.dispatchEvent(new CustomEvent('chat-agent-selected', { detail: agentId }))
    } catch {}
  }

  const lastUpdated = useMemo(() => {
    if (!agents.length) return '—'
    const mostRecent = agents.reduce((acc, agent) => {
      if (!acc) return agent
      return new Date(agent.updated_at) > new Date(acc.updated_at) ? agent : acc
    })
    return dateFormatter.format(new Date(mostRecent.updated_at))
  }, [agents])

  return (
    <div className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.17),transparent_55%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_88%_20%,rgba(168,85,247,0.15),transparent_50%)]" />

      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-6 px-6 py-6">
        {statsDrawerOpen && (
          <>
            <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm" onClick={closeStatsDrawer} aria-hidden />
            <aside className="fixed inset-y-0 right-0 z-[80] w-full max-w-xl border-l border-emerald-100/80 bg-white/95 shadow-2xl transition-transform dark:border-emerald-900/40 dark:bg-zinc-900/95">
              <div className="flex h-full flex-col">
                <header className="flex items-start justify-between gap-4 border-b border-emerald-100/80 px-6 py-4 dark:border-emerald-900/40">
                  <div className="space-y-1">
                    <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Tableau de bord agents</h2>
                    <p className="text-sm text-zinc-600 dark:text-zinc-300">Suivez vos quotas et les agents les plus actifs.</p>
                  </div>
                  <button
                    type="button"
                    onClick={closeStatsDrawer}
                    className="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                  >
                    ✕
                  </button>
                </header>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                  <section className="grid gap-4 sm:grid-cols-2">
                    {overviewStats.map(stat => (
                      <StatCard key={stat.label} label={stat.label} value={stat.value} description={stat.description} />
                    ))}
                  </section>

                  <StatsDetails
                    agents={agents}
                    ownerUsage={ownerUsage}
                    numberFormatter={numberFormatter}
                    dateFormatter={dateFormatter}
                  />
                </div>
              </div>
            </aside>
          </>
        )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={cancelDeleteAgent} />
          <div className="absolute inset-x-0 top-1/2 mx-auto w-full max-w-lg -translate-y-1/2 overflow-hidden rounded-3xl border border-red-200/70 bg-white/95 shadow-2xl shadow-red-300/30 dark:border-red-900/60 dark:bg-zinc-950/95">
            <div className="flex items-center justify-between border-b border-red-200/70 bg-red-50/60 px-6 py-4 dark:border-red-900/60 dark:bg-red-950/40">
              <div>
                <h2 className="text-lg font-semibold text-red-700 dark:text-red-200">Supprimer l’agent</h2>
                <p className="text-sm text-red-600/80 dark:text-red-300/80">Cette action retire définitivement l’agent et ses statistiques.</p>
              </div>
              <button
                onClick={cancelDeleteAgent}
                className="rounded-full border border-red-200/70 px-3 py-1 text-sm text-red-600 transition hover:bg-red-100/60 dark:border-red-800 dark:text-red-200 dark:hover:bg-red-900/60"
              >
                Fermer
              </button>
            </div>
            <div className="space-y-4 px-6 py-6 text-sm text-zinc-700 dark:text-zinc-200">
              <p>
                Êtes-vous sûr de vouloir supprimer
                {' '}
                <span className="font-semibold text-zinc-900 dark:text-white">{deleteTarget.name}</span>
                {' '}<span className="text-xs font-medium text-zinc-400 dark:text-zinc-500">(ID {deleteTarget.id})</span> ?
              </p>
              {deleteTarget.subject_ids?.length ? (
                <div className="rounded-2xl border border-red-200/70 bg-red-50/70 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                  L’agent est lié à {deleteTarget.subject_ids.length} sujet(s). Les médias resteront accessibles mais l’agent ne pourra plus répondre aux conversations.
                </div>
              ) : (
                <div className="rounded-2xl border border-red-200/70 bg-red-50/70 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
                  L’agent sera retiré immédiatement de vos intégrations (chat widget, API, etc.).
                </div>
              )}
              {deleteError && (
                <div className="rounded-2xl border border-red-300 bg-white/90 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-red-200/60 bg-red-50/50 px-6 py-4 dark:border-red-900/50 dark:bg-red-950/30">
              <button
                onClick={cancelDeleteAgent}
                disabled={deletePending}
                className="rounded-full border border-zinc-200/70 px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100/70 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800/60"
              >
                Annuler
              </button>
              <button
                onClick={confirmDeleteAgent}
                disabled={deletePending}
                className="rounded-full border border-red-200/70 bg-red-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-600 disabled:opacity-50 dark:border-red-900/60"
              >
                {deletePending ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
        {/* Create Agent Modal */}
        {createDrawerOpen && (
          <AgentEditorModal
            agent={null}
            subjects={subjects}
            subjectsLoading={subjectsLoading}
            ollamaModels={ollamaModels}
            ollamaModelsLoading={ollamaModelsLoading}
            isAdmin={isAdmin}
            systemPrompts={systemPrompts}
            teams={teams}
            teamsLoading={teamsLoading}
            onClose={(saved) => {
              setCreateDrawerOpen(false)
              if (saved) {
                loadAgents()
                loadQuota()
              }
            }}
            onSave={(savedAgent) => {
              try {
                localStorage.setItem('chat_selected_agent', String(savedAgent.id))
                window.dispatchEvent(new CustomEvent('chat-agent-selected', { detail: savedAgent.id }))
              } catch {}
            }}
          />
        )}


        <section className="flex gap-6">
          {/* Teams Sidebar */}
          <TeamsSidebar
            teams={teams}
            loading={teamsLoading}
            selectedId={selectedTeamId}
            onSelect={setSelectedTeamId}
            totalAgents={agents.length}
            orphanCount={orphanAgentCount}
            agentCountByTeam={agentCountByTeam}
            onCreate={createTeam}
            onRename={renameTeam}
            onDelete={removeTeam}
            createModalOpen={teamModalOpen}
            onCreateModalOpenChange={setTeamModalOpen}
          />

          {/* Main content */}
          <div className="flex-1 space-y-6">
            <div className="rounded-3xl border border-emerald-100/70 bg-white/90 p-6 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">Vos agents</h2>
                  <p className="mt-1 mb-3 text-sm text-zinc-500 dark:text-zinc-400">
                    {agents.length ? `${agents.length} agent(s) — Dernière mise à jour ${lastUpdated}` : 'Aucun agent pour le moment'}
                  </p>
                  
                  {/* Badge de quota d'agents actifs */}
                  {quota && (
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-emerald-50/50 px-4 py-2 text-sm dark:border-emerald-900/50 dark:bg-emerald-900/20">
                      <span className="font-semibold text-emerald-700 dark:text-emerald-300">
                        {quota.current_active} actif{quota.current_active > 1 ? 's' : ''}
                      </span>
                      {quota.max_active !== null && (
                        <>
                          <span className="text-zinc-400 dark:text-zinc-600">/</span>
                          <span className="text-zinc-600 dark:text-zinc-400">
                            {quota.max_active} max
                          </span>
                          {quota.available !== null && quota.available === 0 && (
                            <span className="ml-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                              Quota atteint
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 bg-white/80 px-4 py-2 text-sm font-semibold text-emerald-600 shadow-sm transition hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-900/40 dark:text-emerald-300"
                    onClick={openStatsDrawer}
                  >
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-[11px] font-bold text-white">Σ</span>
                    Statistiques
                  </button>
                  <button
                    type="button"
                    className="rounded-full border border-emerald-200/70 bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600"
                    onClick={openCreateDrawer}
                    data-onboarding="new-agent-btn"
                  >
                    Créer un nouvel agent
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="py-12 text-center text-sm text-zinc-500">Chargement des agents…</div>
              ) : error ? (
                <div className="py-6 text-sm text-red-600 dark:text-red-300">{error}</div>
              ) : agents.length === 0 ? (
                <div className="py-10 text-sm text-zinc-500">
                  Aucun agent pour le moment. Créez votre premier assistant en définissant sa personnalité, son icône et ses bases de connaissances.
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredAgents.map(agent => (
                    <article key={agent.id} className="rounded-2xl border border-zinc-200/70 bg-white/95 p-5 shadow-md dark:border-zinc-800 dark:bg-zinc-900/90">
                      <div className="flex flex-wrap items-start gap-4">
                        <div className="flex items-center gap-3">
                          <AgentAvatar agent={agent} uploading={iconUploadingId === agent.id} onIconChange={onIconChange(agent.id)} />
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">{agent.name}</h3>
                              {agent.is_active ? (
                                <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                  Actif
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                                  Inactif
                                </span>
                              )}
                            </div>
                            <p className="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-300">{agent.slug}</p>
                          </div>
                        </div>
                        <div className="ml-auto flex flex-wrap gap-2 text-xs">
                          <InfoChip label="Messages" value={`${numberFormatter.format(agent.usage.message_count)}${agent.usage.limit != null ? ` / ${numberFormatter.format(agent.usage.limit)}` : ''}`} />
                          <InfoChip label="📚 Docs" value={agent.subject_ids.length ? agent.subject_ids.length.toString() : '0'} />
                          <InfoChip label="🧠 IA" value={agent.model_id || 'Défaut'} />
                          <InfoChip label="Maj" value={dateFormatter.format(new Date(agent.updated_at))} />
                        </div>
                      </div>

                      {agent.instructions && (
                        <p className="mt-3 whitespace-pre-wrap text-sm text-zinc-600 dark:text-zinc-300 line-clamp-4">
                          {agent.instructions}
                        </p>
                      )}

                      <div className="mt-4 flex flex-wrap gap-3">
                        <button
                          type="button"
                          className="rounded-full border border-emerald-200/70 bg-emerald-100 px-4 py-1.5 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-200/70 dark:border-emerald-900/50 dark:bg-emerald-900/40 dark:text-emerald-200"
                          onClick={() => selectForChat(agent.id)}
                        >
                          Utiliser dans le chat
                        </button>
                        <button
                          type="button"
                          className="rounded-full border border-blue-200/70 px-4 py-1.5 text-xs font-semibold text-blue-600 transition hover:bg-blue-50 dark:border-blue-900/50 dark:text-blue-300 dark:hover:bg-blue-950/40"
                          onClick={() => navigate(`/conversations?agent_id=${agent.id}`)}
                        >
                          💬 Conversations
                        </button>
                        <button
                          type="button"
                          className="rounded-full border border-zinc-200/70 px-4 py-1.5 text-xs font-semibold text-zinc-600 transition hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                          onClick={() => startEdit(agent)}
                          data-onboarding="agent-edit-btn"
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition ${
                            agent.is_active
                              ? 'border-orange-200/70 text-orange-600 hover:bg-orange-50 dark:border-orange-900/50 dark:text-orange-300 dark:hover:bg-orange-950/40'
                              : 'border-emerald-200/70 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-900/50 dark:text-emerald-300 dark:hover:bg-emerald-950/40'
                          }`}
                          onClick={() => toggleAgentActivation(agent.id, agent.is_active)}
                        >
                          {agent.is_active ? '⏸ Désactiver' : '▶ Activer'}
                        </button>
                        <button
                          type="button"
                          className="rounded-full border border-emerald-200/70 px-4 py-1.5 text-xs font-semibold text-emerald-600 transition hover:bg-emerald-50 dark:border-emerald-900/50 dark:text-emerald-300 dark:hover:bg-emerald-950/40"
                          onClick={() => openMcpDrawer(agent.id, agent.name)}
                        >
                          🔌 MCP
                        </button>
                        <button
                          type="button"
                          className="rounded-full border border-purple-200/70 px-4 py-1.5 text-xs font-semibold text-purple-600 transition hover:bg-purple-50 dark:border-purple-900/50 dark:text-purple-300 dark:hover:bg-purple-950/40"
                          onClick={() => navigate(`/email?assign_agent=${agent.id}`)}
                        >
                          📧 Assigner email
                        </button>
                        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-zinc-200/70 px-4 py-1.5 text-xs font-semibold text-zinc-600 transition hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800">
                          <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onIconChange(agent.id)} />
                          {iconUploadingId === agent.id ? 'Upload…' : 'Changer icône'}
                        </label>
                        <button
                          type="button"
                          className="rounded-full border border-red-200/70 px-4 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/40"
                          onClick={() => requestDeleteAgent(agent)}
                          disabled={deletePending && deleteTarget?.id === agent.id}
                        >
                          {deletePending && deleteTarget?.id === agent.id ? 'Suppression…' : 'Supprimer'}
                        </button>
                      </div>

                      {agent.subject_ids.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                          {agent.subject_ids.map(id => {
                            const subject = subjectMap.get(id)
                            if (!subject) return null
                            return (
                              <span key={id} className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 px-3 py-1 dark:border-zinc-700">
                                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                {subject.title}
                              </span>
                            )
                          })}
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>

        </section>
      </div>

      {/* MCP Drawer */}
      <AgentMCPDrawer
        open={mcpDrawerOpen}
        onClose={closeMcpDrawer}
        agentId={mcpDrawerAgentId}
        agentName={mcpDrawerAgentName}
      />

      {/* Agent Onboarding Guide */}
      <AgentOnboardingGuide
        isEditModalOpen={isEditModalOpen}
        isCreateModalOpen={createDrawerOpen}
        isTeamModalOpen={teamModalOpen}
      />

      {isEditModalOpen && editingAgent && (
        <div className="fixed inset-0 z-[95]">
          <div className="absolute inset-0 bg-black/70 backdrop-blur" onClick={cancelEdit} aria-hidden />
          <div className="absolute inset-0 flex flex-col">
            <header className="flex items-center justify-between bg-white/90 px-6 py-4 shadow-md backdrop-blur dark:bg-zinc-950/80">
              <div className="flex items-center gap-4">
                {/* Avatar de l'agent */}
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-emerald-500 text-xl font-semibold text-white shadow-lg shadow-emerald-500/30">
                  {editingAgent.icon_preview_url ? (
                    <img src={editingAgent.icon_preview_url} alt={editingAgent.name} className="h-full w-full object-cover" />
                  ) : (
                    <span>{editingAgent.name.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-emerald-500 dark:text-emerald-300">Agent sandbox</p>
                  <h2 className="text-2xl font-semibold text-zinc-900 dark:text-white">{editingAgent.name}</h2>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">Configurez l'agent et testez-le dans le playground.</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="rounded-full border border-zinc-200/70 px-4 py-2 text-sm font-semibold text-zinc-600 transition hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  onClick={cancelEdit}
                >
                  Fermer
                </button>
                <button
                  type="button"
                  className="rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600"
                  onClick={saveEdit}
                  disabled={savingEdit}
                >
                  {savingEdit ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </header>

            <div className="flex-1 overflow-auto bg-gradient-to-br from-emerald-100/30 via-white to-zinc-50 dark:from-zinc-900 dark:via-zinc-950 dark:to-black">
              <div className="flex h-full flex-col gap-4 px-6 py-6">
                <div className="grid flex-1 gap-6 overflow-hidden lg:grid-cols-2">
                  <aside className="overflow-auto rounded-3xl border border-emerald-200/70 bg-white/90 p-5 shadow-xl dark:border-emerald-900/40 dark:bg-zinc-950/80">
                    <h3 className="text-sm font-semibold text-emerald-600 dark:text-emerald-300">Réglages</h3>
                    <EditPanel
                      agentId={editingAgent.id}
                      form={editForm}
                      onChange={setEditForm}
                      subjects={subjects}
                      subjectMap={subjectMap}
                      onToggleSubject={updateEditSubjectIds}
                      onCancel={cancelEdit}
                      onSave={saveEdit}
                      saving={savingEdit}
                      error={editError}
                      subjectsLoading={subjectsLoading}
                      ollamaModels={ollamaModels}
                      isAdmin={isAdmin}
                      systemPrompts={systemPrompts}
                      teams={teams}
                      teamsLoading={teamsLoading}
                    />
                  </aside>

                  <section className="flex h-full flex-col overflow-auto rounded-3xl border border-emerald-200/60 bg-white/95 p-5 shadow-xl dark:border-emerald-900/40 dark:bg-zinc-950/85">
                    <header className="mb-4 flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-zinc-900 dark:text-white">Playground sandbox</h3>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400">Posez vos questions en mode sandbox sans consommer de quota.</p>
                      </div>
                      <span className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600 dark:border-emerald-900/40 dark:bg-emerald-900/40 dark:text-emerald-200">Quota préservé</span>
                    </header>
                    <AgentSandboxChat
                      agent={editingAgent as AgentSummary}
                      conversationId={sandboxConversationId}
                      onConversationId={setSandboxConversationId}
                      onQuotaBypassNotice={() => {
                        /* hook for future analytics */
                      }}
                    />
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

type StatCardProps = {
  label: string
  value: string
  description: string
}

function StatCard({ label, value, description }: StatCardProps) {
  return (
    <div className="rounded-3xl border border-emerald-100/70 bg-white/90 p-5 shadow-lg shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
      <p className="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-300">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-zinc-900 dark:text-white">{value}</p>
      <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">{description}</p>
    </div>
  )
}

type InfoChipProps = { label: string; value: string }

function InfoChip({ label, value }: InfoChipProps) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 px-3 py-1 text-xs text-zinc-600 dark:border-zinc-700 dark:text-zinc-300">
      <span className="text-[10px] uppercase tracking-wide text-zinc-400 dark:text-zinc-500">{label}</span>
      <span className="font-semibold text-zinc-700 dark:text-zinc-100">{value}</span>
    </span>
  )
}

type StatsDetailsProps = {
  agents: Agent[]
  ownerUsage: Agent['owner_usage'] | null
  numberFormatter: Intl.NumberFormat
  dateFormatter: Intl.DateTimeFormat
}

function StatsDetails({ agents, ownerUsage, numberFormatter, dateFormatter }: StatsDetailsProps) {
  const totalMessages = useMemo(
    () => agents.reduce((acc, agent) => acc + (agent.usage?.message_count ?? 0), 0),
    [agents]
  )

  const subjectCount = useMemo(() => {
    const unique = new Set<number>()
    agents.forEach(agent => agent.subject_ids.forEach(id => unique.add(id)))
    return unique.size
  }, [agents])

  const modelCount = useMemo(() => {
    const unique = new Set<string>()
    agents.forEach(agent => {
      if (agent.model_id) unique.add(agent.model_id)
    })
    return unique.size
  }, [agents])

  const lastCreated = useMemo(() => {
    if (!agents.length) return null
    const sorted = [...agents].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    return sorted[0]
  }, [agents])

  const mostActive = useMemo(() => {
    if (!agents.length) return []
    return [...agents]
      .sort((a, b) => (b.usage?.message_count ?? 0) - (a.usage?.message_count ?? 0))
      .slice(0, 5)
  }, [agents])

  const limit = ownerUsage?.limit ?? null
  const ownerCount = ownerUsage?.message_count ?? 0
  const utilisation = limit ? Math.min(Math.round((ownerCount / limit) * 100), 999) : null

  return (
    <div className="space-y-6">
      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-3xl border border-emerald-100/70 bg-white/95 p-5 shadow-lg shadow-emerald-500/10 dark:border-emerald-900/40 dark:bg-zinc-950/90">
          <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Usage global</h3>
          <p className="mt-2 text-3xl font-bold text-emerald-600 dark:text-emerald-300">
            {numberFormatter.format(ownerCount)}
          </p>
          <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">messages envoyés par l’organisation</p>
          <div className="mt-4 space-y-2 text-xs text-zinc-500 dark:text-zinc-400">
            <div className="flex items-center justify-between">
              <span>Total agents</span>
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">{numberFormatter.format(agents.length)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Bases de connaissances</span>
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">{numberFormatter.format(subjectCount)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Intelligences utilisées</span>
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">{numberFormatter.format(modelCount)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Total messages par agents</span>
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">{numberFormatter.format(totalMessages)}</span>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-emerald-100/70 bg-white/95 p-5 shadow-lg shadow-emerald-500/10 dark:border-emerald-900/40 dark:bg-zinc-950/90">
          <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Quota global</h3>
          <div className="mt-3">
            <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-300">
              {limit != null ? numberFormatter.format(limit) : '∞'}
            </p>
            <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">messages autorisés</p>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400">
              <span>Utilisation</span>
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">
                {utilisation != null ? `${utilisation}%` : 'Non plafonné'}
              </span>
            </div>
            {utilisation != null && (
              <div className="mt-2 h-2 rounded-full bg-emerald-100 dark:bg-emerald-950">
                <div
                  className="h-full rounded-full bg-emerald-500"
                  style={{ width: `${Math.max(utilisation, 4)}%` }}
                />
              </div>
            )}
            {limit != null && (
              <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                Reste {numberFormatter.format(Math.max(limit - ownerCount, 0))} messages sur la période.
              </p>
            )}
          </div>
          {lastCreated && (
            <p className="mt-4 text-xs text-zinc-500 dark:text-zinc-400">
              Dernier agent créé :{' '}
              <span className="font-semibold text-zinc-700 dark:text-zinc-200">{lastCreated.name}</span>{' '}
              ({dateFormatter.format(new Date(lastCreated.created_at))})
            </p>
          )}
        </div>
      </section>

      <section className="rounded-3xl border border-emerald-100/70 bg-white/95 p-5 shadow-lg shadow-emerald-500/10 dark:border-emerald-900/40 dark:bg-zinc-950/90">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Agents les plus sollicités</h3>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">Top 5 sur la période courante</span>
        </div>
        {mostActive.length === 0 ? (
          <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">Aucun message envoyé pour le moment.</p>
        ) : (
          <ul className="mt-4 space-y-3 text-sm text-zinc-600 dark:text-zinc-300">
            {mostActive.map(agent => {
              const limitText =
                agent.usage?.limit != null
                  ? `${numberFormatter.format(agent.usage.limit)} max`
                  : 'Sans plafond'
              return (
                <li key={agent.id} className="flex items-start justify-between gap-3 rounded-2xl border border-zinc-200/70 bg-white/80 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/80">
                  <div className="space-y-1">
                    <p className="font-semibold text-zinc-900 dark:text-white">{agent.name}</p>
                    {agent.subtitle && <p className="text-xs text-zinc-500 dark:text-zinc-400">{agent.subtitle}</p>}
                    <p className="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-300">{agent.slug}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{agent.subject_ids.length} doc(s) — {limitText}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-300">
                      {numberFormatter.format(agent.usage?.message_count ?? 0)}
                    </p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">messages</p>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </div>
  )
}

type AgentAvatarProps = {
  agent: Agent
  uploading: boolean
  onIconChange: (event: ChangeEvent<HTMLInputElement>) => void
}

function AgentAvatar({ agent, uploading, onIconChange }: AgentAvatarProps) {
  return (
    <label className="relative inline-flex h-12 w-12 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-emerald-500 text-lg font-semibold text-white shadow-lg shadow-emerald-500/30">
      {agent.icon_preview_url ? (
        <img src={agent.icon_preview_url} alt={agent.name} className="h-full w-full object-cover" />
      ) : (
        <span>{agent.name.charAt(0).toUpperCase()}</span>
      )}
      <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={onIconChange} />
      {uploading && (
        <span className="absolute inset-0 flex items-center justify-center bg-white/70 text-xs text-emerald-600">Upload…</span>
      )}
    </label>
  )
}

type EditPanelProps = {
  agentId: number
  form: FormState
  onChange: (state: FormState) => void
  subjects: Subject[]
  subjectMap: Map<number, Subject>
  onToggleSubject: (id: number) => void
  onCancel: () => void
  onSave: () => void
  saving: boolean
  error: string | null
  subjectsLoading: boolean
  ollamaModels: string[]
  isAdmin?: boolean
  systemPrompts?: SystemPromptListItem[]
  teams?: { id: number; title: string }[]
  teamsLoading?: boolean
}

function EditPanel({ agentId, form, onChange, subjects, onToggleSubject, onCancel, onSave, saving, error, subjectsLoading, ollamaModels, isAdmin, systemPrompts, teams, teamsLoading }: EditPanelProps) {
  const [activeTab, setActiveTab] = useState<'general' | 'knowledge' | 'whatsapp'>('general')
  
  // Paramètres avancés - persisté en localStorage (admin uniquement)
  const [showAdvanced, setShowAdvanced] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('agent_show_advanced') === 'true'
    }
    return false
  })
  
  const toggleAdvanced = () => {
    const newValue = !showAdvanced
    setShowAdvanced(newValue)
    localStorage.setItem('agent_show_advanced', String(newValue))
  }
  
  return (
    <div className="mt-3 rounded-2xl border border-emerald-100/70 bg-white/90 p-3 text-sm shadow-inner dark:border-emerald-900/50 dark:bg-zinc-900/80">
      <h4 className="text-sm font-semibold text-zinc-900 dark:text-white mb-3">Modifier l'agent</h4>
      
      {/* Onglets */}
      <div className="flex gap-2 mb-3 border-b border-gray-200 dark:border-zinc-700">
        <button
          type="button"
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 text-sm font-medium transition border-b-2 ${
            activeTab === 'general'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          Général
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('knowledge')}
          className={`px-4 py-2 text-sm font-medium transition border-b-2 ${
            activeTab === 'knowledge'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
          data-onboarding="knowledge-tab"
        >
          Base de connaissances
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('whatsapp')}
          className={`px-4 py-2 text-sm font-medium transition border-b-2 ${
            activeTab === 'whatsapp'
              ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          WhatsApp
        </button>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'general' ? (
      <div className="grid gap-3">
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Nom</label>
          <input
            className="w-full rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:text-zinc-200"
            value={form.name}
            onChange={e => onChange({ ...form, name: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Sous-titre</label>
          <input
            className="w-full rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:text-zinc-200"
            value={form.subtitle}
            onChange={e => onChange({ ...form, subtitle: e.target.value })}
            placeholder="Tagline visible dans le chat"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">👥 Équipe</label>
          <select
            className="w-full rounded-xl border border-emerald-100/70 bg-white/80 px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:bg-zinc-900/70 dark:text-zinc-200"
            value={form.teamId ?? ''}
            onChange={e => onChange({ ...form, teamId: e.target.value ? Number(e.target.value) : null })}
            disabled={teamsLoading}
          >
            <option value="">Sans équipe</option>
            {teams?.map(team => (
              <option key={team.id} value={team.id}>{team.title}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">💬 Personnalité</label>
          <textarea
            className="h-16 w-full rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:text-zinc-200"
            value={form.instructions}
            onChange={e => onChange({ ...form, instructions: e.target.value })}
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">Limite</label>
            <input
              type="number"
              min={1}
              className="w-full rounded-xl border border-emerald-100/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:text-zinc-200"
              value={form.messageLimit}
              onChange={e => onChange({ ...form, messageLimit: e.target.value })}
              placeholder="Aucun plafond"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 flex items-center">
              🧠 Intelligence
              <HelpTooltip content="Plus le niveau est élevé, plus les réponses sont précises mais peuvent être plus lentes." position="right" />
            </label>
            <select
              className="w-full rounded-xl border border-emerald-100/70 bg-white/80 px-3 py-2 text-sm text-zinc-700 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-200 dark:border-emerald-900/60 dark:bg-zinc-900/70 dark:text-zinc-200"
              value={form.modelId}
              onChange={e => onChange({ ...form, modelId: e.target.value })}
            >
              <option value="">Défaut (système)</option>
              {ollamaModels.map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
        </div>
        {error && <p className="text-xs text-red-600 dark:text-red-300">{error}</p>}
      </div>
      ) : activeTab === 'knowledge' ? (
      <div className="grid gap-3">
        <div className="space-y-1">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.disableOwnKnowledge}
              onChange={e => onChange({ ...form, disableOwnKnowledge: e.target.checked })}
              className="h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 flex items-center">
              Réponses strictes
              <HelpTooltip content="Si activé, l'assistant ne répondra QU'avec les informations de vos documents. Il ne pourra pas utiliser ses connaissances générales." position="right" />
            </span>
          </label>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Si activé, l'assistant ne répondra qu'avec les informations de votre base de connaissances.
          </p>
        </div>
        <div className="space-y-1" data-onboarding="knowledge-base-section">
          <label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 flex items-center">
            Base de connaissances
            <HelpTooltip content="Documents et fichiers que votre assistant peut consulter pour répondre aux questions." position="right" />
          </label>
          <div className="max-h-28 overflow-y-auto rounded-2xl border border-zinc-200/70 bg-white/80 p-2 dark:border-zinc-800 dark:bg-zinc-900/80">
            {subjectsLoading && <div className="text-xs text-zinc-500">Chargement…</div>}
            {!subjectsLoading && subjects.length === 0 && <div className="text-xs text-zinc-500">Aucune base de connaissances disponible.</div>}
            {!subjectsLoading && subjects.map(subject => (
              <label key={subject.id} className="flex items-center gap-2 py-1">
                <input type="checkbox" checked={form.subjectIds.includes(subject.id)} onChange={() => onToggleSubject(subject.id)} />
                <span className="text-xs text-zinc-600 dark:text-zinc-300">{subject.title}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Sélectionnez les documents que l'assistant peut consulter pour répondre.
          </p>
        </div>

        {/* Toggle Paramètres avancés (admin uniquement) */}
        {isAdmin && (
          <div className="mt-2 pt-2 border-t border-zinc-200/50 dark:border-zinc-700/50">
            <button
              type="button"
              onClick={toggleAdvanced}
              className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200 transition"
            >
              <span className={`transform transition-transform ${showAdvanced ? 'rotate-90' : ''}`}>▶</span>
              <span className="uppercase tracking-wide">Paramètres avancés</span>
              {showAdvanced && <span className="text-violet-500 dark:text-violet-400">(admin)</span>}
            </button>
            
            {showAdvanced && (
              <div className="mt-3 space-y-3 pl-4 border-l-2 border-violet-200/50 dark:border-violet-800/50">
                {/* Top K */}
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-violet-600 dark:text-violet-400">
                    🔒 Top K
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    className="w-full rounded-xl border border-violet-200/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-violet-900/60 dark:text-zinc-200"
                    value={form.topK}
                    onChange={e => onChange({ ...form, topK: e.target.value })}
                    placeholder="10"
                  />
                  <p className="text-xs text-violet-600/70 dark:text-violet-400/70">
                    Nombre de contextes RAG. Auto-configuré selon le modèle pour les utilisateurs.
                  </p>
                </div>

                {/* Rerank Top N */}
                <div className="space-y-1">
                  <label className="text-xs uppercase tracking-wide text-violet-600 dark:text-violet-400">
                    🎯 Rerank Top N
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    className="w-full rounded-xl border border-violet-200/70 bg-transparent px-3 py-2 text-sm text-zinc-700 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-violet-900/60 dark:text-zinc-200"
                    value={form.rerankTopN}
                    onChange={e => onChange({ ...form, rerankTopN: e.target.value })}
                    placeholder="5"
                  />
                  <p className="text-xs text-violet-600/70 dark:text-violet-400/70">
                    Résultats finaux après reranking sémantique (1-20, défaut: 5).
                  </p>
                </div>

                {/* System Prompt Override */}
                {systemPrompts && systemPrompts.length > 0 && (
                  <div className="space-y-1">
                    <label className="text-xs uppercase tracking-wide text-violet-600 dark:text-violet-400">
                      🔒 Prompt Système
                    </label>
                    <select
                      className="w-full rounded-xl border border-violet-200/70 bg-white/80 px-3 py-2 text-sm text-zinc-700 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 dark:border-violet-900/60 dark:bg-zinc-900/70 dark:text-zinc-200"
                      value={form.systemPromptId ?? ''}
                      onChange={e => onChange({ ...form, systemPromptId: e.target.value ? Number(e.target.value) : null })}
                    >
                      <option value="">— Prompt par défaut —</option>
                      {systemPrompts.map(prompt => (
                        <option key={prompt.id} value={prompt.id}>
                          {prompt.name} {prompt.is_default ? '(défaut)' : ''} — v{prompt.version}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-violet-600/70 dark:text-violet-400/70">
                      Remplace le prompt système par défaut pour cet agent.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {error && <p className="text-xs text-red-600 dark:text-red-300">{error}</p>}
      </div>
      ) : (
        <WhatsAppTab agentId={agentId} />
      )}

      {/* Boutons (communs aux onglets General et Knowledge) */}
      {(activeTab === 'general' || activeTab === 'knowledge') && (
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            className="rounded-full border border-emerald-200/70 bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600 disabled:opacity-50"
            onClick={onSave}
            disabled={saving}
            data-onboarding="agent-save-btn"
          >
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button
            type="button"
            className="rounded-full border border-zinc-200/70 px-4 py-1.5 text-xs font-semibold text-zinc-600 transition hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
            onClick={onCancel}
          >
            Annuler
          </button>
        </div>
      )}
    </div>
  )
}
